import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { MEDIA_QUERIES, SPACER } from 'radiance-ui/lib/constants';
import { LOCAL_COLORS } from 'styles/constants';
import { desktopOnly, mobileOnly } from 'styles/mixins/displayMobileDesktop';
import getContrastingColor from 'utils/getContrastingColor';

export const IngredientContainer = styled.div<{ backgroundColor: string }>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ backgroundColor }) => getContrastingColor(backgroundColor)};

  h2,
  p {
    color: ${({ backgroundColor }) => getContrastingColor(backgroundColor)};
  }
`;

export const InnerBox = styled.div`
  padding: ${SPACER.x4large} 0;
  margin: 0 auto;

  ${MEDIA_QUERIES.xlUp} {
    max-width: 1296px;
    padding: 100px 0;
    margin: auto;
  }

  ${MEDIA_QUERIES.xlUp} {
    padding: 100px 72px;
  }

  ${MEDIA_QUERIES.xxlUp} {
    padding: 120px 0;
  }
`;

export const TextBox = styled.div`
  margin: 0 ${SPACER.medium};

  ${MEDIA_QUERIES.mdUp} {
    margin: 0 ${SPACER.xlarge};
  }

  ${MEDIA_QUERIES.xlUp} {
    margin: 0;
  }
`;

export const TextBoxHeader = styled.h2`
  font-size: 32px;
  font-weight: 700;
  line-height: 110%;
  letter-spacing: -0.64px;
  margin-bottom: ${SPACER.large};

  ${MEDIA_QUERIES.mdUp} {
    font-size: 32px;
    max-width: 400px;
  }

  ${MEDIA_QUERIES.xlUp} {
    font-size: 52px;
    max-width: 650px;
  }
`;

export const TextBoxBody = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

export const TextBoxBodyContent = styled.div`
  padding: 0;
  display: flex;
  align-items: center;

  ${MEDIA_QUERIES.mdUp} {
    font-size: 16px;
    line-height: 132%;
    max-width: 800px;
  }

  ${MEDIA_QUERIES.xxlUp} {
    font-size: 16px;
    line-height: 132%;
    max-width: 900px;
  }

  ${MEDIA_QUERIES.xlUp} {
    font-size: ${SPACER.large};
    line-height: 140%;
  }

  svg {
    margin-right: ${SPACER.medium};
    width: ${SPACER.large};
    height: ${SPACER.large};
  }

  p {
    font-weight: 700;
  }

  span.gold {
    color: #ff824f;
  }
`;

export const desktopCtaStyles = css`
  ${desktopOnly()}

  button {
    font-size: 16px;
    line-height: 132%;
    letter-spacing: -0.32px;
  }
`;

export const mobileCtaStyles = css`
  ${mobileOnly()}
  margin: 0 auto;
`;

export const IngredientBox = styled.div<{ size: number }>`
  display: flex;
  flex-flow: ${({ size }) => (size <= 2 ? 'column' : 'row nowrap')};
  justify-content: ${({ size }) =>
    size <= 2 ? 'space-between' : 'flex-start'};
  align-items: center;
  overflow: auto;
  padding: 0 ${SPACER.medium} ${SPACER.medium};
  margin: 44px 0;
  ${({ size }) => (size <= 2 ? `gap: ${SPACER.large}` : '')};

  & > :nth-of-type(2) p {
    width: 100%;
  }

  & > :last-of-type {
    border-right: 2px solid ${LOCAL_COLORS.surfaceDarkBold};
  }

  ${MEDIA_QUERIES.mdUp} {
    padding: 0 ${SPACER.xlarge} ${SPACER.medium};
    flex-flow: row nowrap;

    ${({ size }) =>
      size === 5 &&
      `
        & > :nth-of-type(4) {
          border-right: 2px solid ${LOCAL_COLORS.surfaceDarkBold};
        }

        & > :nth-of-type(5) {
          width: 261px;
          border-top: 0px solid ${LOCAL_COLORS.surfaceDarkBold};
        }
    `}
  }

  ${MEDIA_QUERIES.xlUp} {
    flex-wrap: wrap;
    padding: 0;
    ${({ size }) =>
      size < 5 &&
      `
        & > :nth-of-type(4) {
          border-right: 2px solid ${LOCAL_COLORS.surfaceDarkBold};
        }
    
        & > :nth-of-type(5),
        & > :nth-of-type(6),
        & > :nth-of-type(7) {
          border-top: 0px solid ${LOCAL_COLORS.surfaceDarkBold};
        }

        & > :last-of-type {
          width: 261px;
        }
    `}
  }

  ${({ size }) =>
    size <= 5 &&
    `& > div, & > :last-of-type {
      width: 100%;

      ${MEDIA_QUERIES.mdUp} {
        width: calc((100% - ${SPACER.large}) / 2);
      }

      & > div {
        width: 100%;
      }
    }`};

  ${MEDIA_QUERIES.xlUp} {
    ${({ size }) =>
      size < 5
        ? `
       & > :nth-of-type(4) {
         border-right: none;
       }
   
       & > :nth-of-type(5) {
         border-top: 2px solid ${LOCAL_COLORS.surfaceDarkBold};
         border-right: 2px solid ${LOCAL_COLORS.surfaceDarkBold};
       }
   `
        : `
        & > :nth-of-type(4) {
          border-right: 2px solid ${LOCAL_COLORS.surfaceDarkBold};
        }
    `}
  }
`;

export const Disclaimer = styled.small`
  white-space: wrap;
  margin: 60px ${SPACER.medium} ${SPACER.large};

  ${MEDIA_QUERIES.mdUp} {
    margin: 0 ${SPACER.xlarge};
    padding-left: 0;
  }

  ${MEDIA_QUERIES.xlUp} {
    margin: ${SPACER.large} 0 0;
  }
`;
